import React, { useState } from "react";
import {
  TextInput,
  Box,
  Text,
  Tooltip,
  useMantineTheme,
  Input,
  PasswordInput,
  ActionIcon,
  rem,
} from "@mantine/core";
import { IconLock, IconEye, IconEyeOff } from "@tabler/icons-react";

// Custom masked input that looks like password but isn't treated as one by browsers
const MaskedInput = ({
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  error,
  size = "xl",
  ...props
}) => {
  const [showText, setShowText] = useState(false);
  const theme = useMantineTheme();
  const iconColor =
    theme.colorScheme === "dark" ? theme.colors.gray[2] : theme.colors.gray[7];
  const iconHoverColor =
    theme.colorScheme === "dark" ? theme.white : theme.black;
  const iconHoverBackground =
    theme.colorScheme === "dark"
      ? theme.colors.dark[5]
      : theme.colors.gray[1];
  
  const toggleVisibility = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setShowText((prevState) => !prevState);
  };
  
  // Ensure only numeric characters are persisted while still allowing IME composition
  const handleChange = (event) => {
    if (!onChange) {
      return;
    }

    // React may reuse the synthetic event, so persist it before mutating
    if (event.persist) {
      event.persist();
    }

    const digitsOnly = event.target.value.replace(/\D/g, "");

    // Update the underlying input so the cursor position stays aligned
    if (digitsOnly !== event.target.value) {
      event.target.value = digitsOnly;
    }

    onChange(event);
  };
  
  // Force -webkit-text-security to work in an inline style
  const maskStyle = !showText ? {
    WebkitTextSecurity: "disc",
    MozTextSecurity: "disc", 
    textSecurity: "disc",
    fontFamily: "text-security-disc" // Fallback for some browsers
  } : {};
  
  // Define a custom class for masked input
  const maskedClass = !showText ? "masked-input" : "";
  
  const toggleLabel = showText ? "Hide SSN" : "Show SSN";

  return (
    <div className="masked-input-container" style={{ position: "relative" }}>
      <style>
        {`
        .masked-input {
          -webkit-text-security: disc !important;
          -moz-text-security: disc !important;
          text-security: disc !important;
        }
        
        /* Force numeric input styling */
        input[type="tel"]::-webkit-outer-spin-button,
        input[type="tel"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        
        input[type="tel"] {
          -moz-appearance: textfield;
        }
        `}
      </style>
      <TextInput
        label={label}
        value={value}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        error={error}
        size={size}
        // Force specific attributes to ensure they're not overridden by {...props}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        data-lpignore="true"
        // Add direct HTML attributes as a last resort
        inputProps={{
          inputmode: "numeric", // HTML attribute (lowercase)
          pattern: "[0-9]*",
          type: "tel"
        }}
        {...props} // Include other props after our forced ones
        classNames={{ input: maskedClass }}
        rightSection={
          <Tooltip label={toggleLabel} withArrow position="left">
            <ActionIcon
              onMouseDown={toggleVisibility}
              tabIndex={-1}
              variant="subtle"
              aria-label={toggleLabel}
              aria-pressed={showText}
              sx={{
                cursor: "pointer",
                zIndex: 100,
                color: iconColor,
                "&:hover": {
                  color: iconHoverColor,
                  backgroundColor: iconHoverBackground,
                },
              }}
            >
              {showText ? (
                <IconEyeOff size="1.1rem" stroke={1.75} />
              ) : (
                <IconEye size="1.1rem" stroke={1.75} />
              )}
            </ActionIcon>
          </Tooltip>
        }
        styles={(mantineTheme) => ({
          input: {
            fontSize: "20px",
            paddingTop: "18px",
            ...maskStyle // Apply inline mask style
          },
          rightSection: {
            width: rem(42),
            display: "flex",
            justifyContent: "center",
            pointerEvents: "auto",
            cursor: "pointer",
            zIndex: 10,
            color: iconColor,
          },
          ...(props.styles && props.styles(mantineTheme))
        })}
      />
    </div>
  );
};

const FloatingLabelInput = ({
  label,
  tooltip,
  onChange,
  value: propValue,
  customOnChange,
  component,
  isPassword = false,
  isMaskedInput = false, // New prop for sensitive data that shouldn't be saved by browsers
  autoComplete = "off",
  ...props
}) => {
  const [focused, setFocused] = useState(false);

  // Determine if the label should float
  const floating = focused || (propValue && propValue.length > 0);

  const handleChange = (event) => {
    // Run custom logic if provided
    if (customOnChange) {
      customOnChange(event);
    } else {
      // Otherwise, just use the provided onChange
      onChange && onChange(event);
    }
  };

  // Choose the right input component
  let InputComponent;
  if (isMaskedInput) {
    // Use our custom masked input for sensitive data
    InputComponent = MaskedInput;
  } else if (isPassword) {
    // Use regular password input for actual passwords
    InputComponent = PasswordInput;
  } else {
    // Default to regular text input
    InputComponent = TextInput;
  }

  const InputComponentReturn = (
    <InputComponent
      {...props}
      size={"xl"}
      value={propValue}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={handleChange}
      label={label}
      placeholder={floating ? props.placeholder : ""}
      autoComplete={autoComplete}
      data-lpignore="true"
      styles={(theme) => ({
        input: {
          fontSize: "20px",
          paddingTop: "18px",
        },
        innerInput: {
          fontSize: "20px",
          paddingTop: "18px",
        },
        label: {
          position: "absolute",
          pointerEvents: "none",
          fontSize: "11px",
          paddingLeft: "10px",
          paddingTop: "5px",
          zIndex: "1",
          color: "#868e96",
        },
        root: {
          position: "relative",
        },
        error: {
          fontSize: "12px",
        },
      })}
    />
  );

  return InputComponentReturn;
};

export default FloatingLabelInput;
